import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, Award, Users, DollarSign } from 'lucide-react';
import SlideshowBackground from '../components/SlideshowBackground';
import { useColleges } from '../context/CollegeContext';

interface College {
  name: string;
  location: string;
  fees: string;
  duration: string;
  seats: number;
  established: string;
  cutoff: string;
  university: string;
  minority?: string;
}

const MBBSPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('Government');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Get medical colleges from context
  const { colleges: contextColleges } = useColleges();
  const medicalColleges = useMemo(() => 
    contextColleges.filter((college: any) => college.type === 'medical'), 
    [contextColleges]
  );

  const categories = ['Government', 'Deemed University', 'Self Financing Colleges'];

  // Display context colleges first, then fallback to existing data
  const dynamicColleges: College[] = medicalColleges.map((college: any) => ({
    name: college.name,
    location: college.location,
    fees: college.fees,
    duration: "5.5 years", // Default duration
    seats: college.seats,
    established: college.established,
    cutoff: "NEET: Check college website",
    university: college.accreditation,
  }));

  const colleges: Record<string, College[]> = {
    Government: [
      // Add dynamic colleges from admin dashboard
      ...dynamicColleges,
      {
        name: "All India Institute of Medical Sciences, Madurai",
        location: "Madurai",
        fees: "₹8,000 per year",
        duration: "5.5 years",
        seats: 100,
        established: "2021",
        cutoff: "NEET: 99.9+ percentile",
        university: "Statutory Autonomous, AIIMS (Madurai)",

      },
      {
        name: "Chengalpattu Medical College",
        location: "Chengalpattu",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 200,
        established: "1965",
        cutoff: "NEET: 96+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Coimbatore Medical College",
        location: "Coimbatore",
        fees: "₹48,000 per year",
        duration: "5.5 years",
        seats: 250,
        established: "1966",
        cutoff: "NEET: 95+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "ESIC Medical College & PGIMSR, K.K Nagar",
        location: "Chennai",
        fees: "₹45,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2013",
        cutoff: "NEET: 94+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Dharmapuri Medical College",
        location: "Dharmapuri",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2008",
        cutoff: "NEET: 93+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Erode Medical College & Hospital",
        location: "Perundurai (Erode)",
        fees: "₹48,000 per year",
        duration: "5.5 years",
        seats: 200,
        established: "1992",
        cutoff: "NEET: 94+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College & ESIC Hospital",
        location: "Coimbatore",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2016",
        cutoff: "NEET: 92+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College Tiruppur",
        location: "Tiruppur",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 91+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College Kallakurichi",
        location: "Kallakurichi",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 90+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College Krishnagiri",
        location: "Krishnagiri",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 90+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College Nagapattinam",
        location: "Nagapattinam",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 89+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College Ramanathapuram",
        location: "Ramanathapuram",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 89+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College, Karur",
        location: "Karur",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2019",
        cutoff: "NEET: 91+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College, Namakkal",
        location: "Namakkal",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 90+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College, Omandurar",
        location: "Chennai",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2015",
        cutoff: "NEET: 93+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College, Pudukottai",
        location: "Pudukottai",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2017",
        cutoff: "NEET: 91+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College, The Nilgiris",
        location: "The Nilgiris",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 100,
        established: "2021",
        cutoff: "NEET: 88+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College, Thiruvallur",
        location: "Thiruvallur",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 90+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College, Virudhunagar",
        location: "Virudhunagar",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 89+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College, Ariyalur",
        location: "Ariyalur",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 88+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Medical College, Dindigul",
        location: "Dindigul",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 89+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Sivagangai Medical College",
        location: "Sivaganga",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2012",
        cutoff: "NEET: 92+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Thiruvannamalai Medical College",
        location: "Thiruvannamalai",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2013",
        cutoff: "NEET: 91+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Vellore Medical College",
        location: "Vellore",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2005",
        cutoff: "NEET: 93+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Government Villupuram Medical College",
        location: "Villupuram",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2010",
        cutoff: "NEET: 92+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Govt. Mohan Kumaramangalam Medical College",
        location: "Salem",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 200,
        established: "1986",
        cutoff: "NEET: 94+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "K A P Viswanathan Government Medical College",
        location: "Trichy",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 200,
        established: "1998",
        cutoff: "NEET: 93+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "KanyaKumari Government Medical College",
        location: "Asaripallam",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2003",
        cutoff: "NEET: 92+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Kilpauk Medical College",
        location: "Chennai",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 200,
        established: "1960",
        cutoff: "NEET: 95+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Madras Medical College",
        location: "Chennai",
        fees: "₹45,000 per year",
        duration: "5.5 years",
        seats: 250,
        established: "1835",
        cutoff: "NEET: 97+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Madurai Medical College",
        location: "Madurai",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 250,
        established: "1954",
        cutoff: "NEET: 95+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Rajah Muthiah Medical College",
        location: "Annamalainagar",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "1985",
        cutoff: "NEET: 93+ percentile",
        university: "Annamalai University",

      },
      {
        name: "Stanley Medical College",
        location: "Chennai",
        fees: "₹45,000 per year",
        duration: "5.5 years",
        seats: 250,
        established: "1838",
        cutoff: "NEET: 96+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Thanjavur Medical College",
        location: "Thanjavur",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 200,
        established: "1959",
        cutoff: "NEET: 94+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Theni Government Medical College",
        location: "Theni",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2006",
        cutoff: "NEET: 92+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Thiruvarur Govt. Medical College",
        location: "Thiruvarur",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2010",
        cutoff: "NEET: 91+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Thoothukudi Medical College",
        location: "Thoothukudi",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 150,
        established: "2000",
        cutoff: "NEET: 93+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Tirunelveli Medical College",
        location: "Tirunelveli",
        fees: "₹50,000 per year",
        duration: "5.5 years",
        seats: 200,
        established: "1965",
        cutoff: "NEET: 94+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      }
    ],
    'Deemed University': [
      {
        name: "ACS Medical College and Hospital",
        location: "Chennai",
        fees: "₹24 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2008",
        cutoff: "NEET: 85+ percentile",
        university: "Dr. MGR Educational and Research Institute (Deemed University), Chennai",

      },
      {
        name: "Bhaarat Medical College & Hospital",
        location: "Chennai",
        fees: "₹26 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2020",
        cutoff: "NEET: 80+ percentile",
        university: "Bharath Institute of Higher Education & Research (Deemed Univ.), Chennai",

      },
      {
        name: "Chettinad Hospital & Research Institute",
        location: "Kanchipuram",
        fees: "₹28 Lakhs per year",
        duration: "5.5 years",
        seats: 200,
        established: "2006",
        cutoff: "NEET: 87+ percentile",
        university: "Chettinad Academy of Research and Education (Deemed University), Chettinad",

      },
      {
        name: "Faculty of Medicine, Sri Lalithambigai Medical College",
        location: "Chennai",
        fees: "₹30 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 85+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Meenakshi Medical College and Research Institute",
        location: "Enathur",
        fees: "₹27 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2003",
        cutoff: "NEET: 86+ percentile",
        university: "Meenakshi University (Deemed), Chennai",

      },
      {
        name: "Saveetha Medical College and Hospital",
        location: "Kanchipuram",
        fees: "₹25 Lakhs per year",
        duration: "5.5 years",
        seats: 200,
        established: "2008",
        cutoff: "NEET: 84+ percentile",
        university: "Saveetha University (Deemed), Chennai",

      },
      {
        name: "Shri Sathya Sai Medical College and Research Institute",
        location: "Kancheepuram",
        fees: "₹23 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2008",
        cutoff: "NEET: 83+ percentile",
        university: "Sri Balaji Vidyapeeth (Deemed University), Pondicherry",

      },
      {
        name: "Sree Balaji Medical College and Hospital",
        location: "Chennai",
        fees: "₹26 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2003",
        cutoff: "NEET: 85+ percentile",
        university: "Bharath Institute of Higher Education & Research (Deemed Univ.), Chennai",

      },
      {
        name: "Sri Ramachandra Medical College & Research Institute",
        location: "Chennai",
        fees: "₹29 Lakhs per year",
        duration: "5.5 years",
        seats: 200,
        established: "1985",
        cutoff: "NEET: 88+ percentile",
        university: "Sri Ramachandra Institute of Higher Education & Research(Deemed to be University), Chennai",

      },
      {
        name: "SRM Medical College Hospital & Research Centre",
        location: "Chengalpattu",
        fees: "₹32 Lakhs per year",
        duration: "5.5 years",
        seats: 200,
        established: "2005",
        cutoff: "NEET: 89+ percentile",
        university: "SRM Institute of Science & Technology (Formerly SRM University)",

      },
      {
        name: "VELS Medical College & Hospital",
        location: "Chennai",
        fees: "₹28 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 82+ percentile",
        university: "Vels Institute Of Science, Technology & Advanced Studies( VISTAS) ( Deemed to be University)",

      },
      {
        name: "Vinayaka Missions Kirupananda Variyar Medical College",
        location: "Salem",
        fees: "₹25 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "1996",
        cutoff: "NEET: 86+ percentile",
        university: "Vinayaka Missions University (Deemed), Salem",

      }
    ],
    'Self Financing Colleges': [
      {
        name: "Annapoorna Medical College & Hospital",
        location: "Salem",
        fees: "₹25 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2011",
        cutoff: "NEET: 85+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Arunai Medical College And Hospital",
        location: "Mathur Tiruvannamalai",
        fees: "₹28 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 80+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Christian Medical College",
        location: "Vellore",
        fees: "₹18 Lakhs per year",
        duration: "5.5 years",
        seats: 100,
        established: "1942",
        cutoff: "NEET: 92+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",
        minority: "Christian Minority",

      },
      {
        name: "Dhanalakshmi Srinivasan Medical College and Hospital",
        location: "Perambalur",
        fees: "₹25 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2011",
        cutoff: "NEET: 83+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Dhanalakshmi Srinivasan Institute of Medical Sciences",
        location: "Perambalur",
        fees: "₹26 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2023",
        cutoff: "NEET: 82+ percentile",
        university: "Dhanalakshmi Srinivasan University, a state private University of Tamil Nadu",

      },
      {
        name: "Indira Medical College & Hospitals",
        location: "Thiruvallur",
        fees: "₹27 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2020",
        cutoff: "NEET: 81+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Karpaga Vinayaga Institute of Medical Sciences",
        location: "Maduranthagam",
        fees: "₹26 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2009",
        cutoff: "NEET: 84+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Karpagam Faculty of Medical Sciences & Research",
        location: "Coimbatore",
        fees: "₹27 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2012",
        cutoff: "NEET: 84+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",
        minority: "Telugu Linguistic Minority",

      },
      {
        name: "KMCH Institute of Health Sciences and Research",
        location: "Coimbatore",
        fees: "₹28 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2019",
        cutoff: "NEET: 86+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Madha Medical College and Hospital",
        location: "Thandalam, Chennai",
        fees: "₹24 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2011",
        cutoff: "NEET: 86+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",
        minority: "Christian Minority",

      },
      {
        name: "Melmaruvathur Adiparasakthi Instt. Medical Sciences and Research",
        location: "Melmaruvathur",
        fees: "₹25 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2008",
        cutoff: "NEET: 83+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Nandha Medical College & Hospital",
        location: "Erode",
        fees: "₹27 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2023",
        cutoff: "NEET: 80+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Panimalar Medical College Hospital & Research Institute",
        location: "Chennai",
        fees: "₹28 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2020",
        cutoff: "NEET: 85+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",
        minority: "Christian Minority",

      },
      {
        name: "PSG Institute of Medical Sciences",
        location: "Coimbatore",
        fees: "₹22 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "1985",
        cutoff: "NEET: 88+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "PSP Medical College Hospital and Research Institute",
        location: "Chennai",
        fees: "₹29 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2023",
        cutoff: "NEET: 82+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Sree Mookambika Institute of Medical Sciences",
        location: "Kanyakumari",
        fees: "₹23 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2006",
        cutoff: "NEET: 81+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",
        minority: "Malayalam Linguistic Minority",

      },
      {
        name: "Sri Muthukumaran Medical College",
        location: "Chennai",
        fees: "₹26 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2010",
        cutoff: "NEET: 83+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",
        minority: "Telugu Linguistic Minority",

      },
      {
        name: "Sri Venkateswaraa Medical College Hospital and Research Institute",
        location: "Chennai",
        fees: "₹28 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2022",
        cutoff: "NEET: 84+ percentile",
        university: "Sri Venkateswaraa University, Chennai",
        minority: "Telugu Linguistic Minority",

      },
      {
        name: "Srinivasan Medical College and Hospital",
        location: "Samayapuram Tiruchirappalli",
        fees: "₹27 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 82+ percentile",
        university: "Dhanalakshmi Srinivasan University",
        minority: "Telugu Linguistic Minority",

      },
      {
        name: "ST Peters Medical College, Hospital & Research Institute",
        location: "Hosur, Krishnagiri",
        fees: "₹26 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 81+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Swamy Vivekanandha Medical College Hospital And Research Institute",
        location: "Tiruchengode, Namakkal",
        fees: "₹27 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2021",
        cutoff: "NEET: 80+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Tagore Medical College and Hospital",
        location: "Chennai",
        fees: "₹28 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2010",
        cutoff: "NEET: 84+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      },
      {
        name: "Trichy SRM Medical College Hospital & Research Centre",
        location: "Trichy",
        fees: "₹29 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2009",
        cutoff: "NEET: 87+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",
        minority: "Telugu Linguistic Minority",

      },
      {
        name: "Velammal Medical College Hospital and Research Institute",
        location: "Madurai",
        fees: "₹26 Lakhs per year",
        duration: "5.5 years",
        seats: 150,
        established: "2013",
        cutoff: "NEET: 83+ percentile",
        university: "The Tamilnadu Dr. MGR Medical University, Chennai",

      }
    ]
  };

  const filteredColleges = colleges[selectedCategory as keyof typeof colleges]?.filter(college =>
    college.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    college.location.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const getTotalSeats = (category: string) => {
    return colleges[category as keyof typeof colleges]?.reduce((total, college) => total + college.seats, 0) || 0;
  };

  const getFeeRange = (category: string) => {
    if (category === 'Government') return '₹8K-50K';
    if (category === 'Deemed University') return '₹23L-32L';
    return '₹18L-29L';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Slideshow */}
      <section className="relative h-96 overflow-hidden">
        <SlideshowBackground />
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              MBBS Colleges in Tamil Nadu 2025
            </h1>
            <p className="text-xl md:text-2xl text-green-100">
              Complete list of all 74 Medical colleges with official data
            </p>
          </motion.div>
        </div>
      </section>

      {/* TNEA Admission Status Banner */}
      <div className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white py-4 overflow-hidden relative z-10 shadow-lg">
        <div className="absolute inset-0 bg-black/20"></div>
        <motion.div 
          className="whitespace-nowrap relative z-20 flex items-center"
          initial={{ x: "100vw" }}
          animate={{ x: "-100%" }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
            repeatType: "loop"
          }}
        >
          <div className="flex items-center space-x-8 text-xl font-bold tracking-wide">
            <span>🚨 IMPORTANT: Second Round Counseling was finished , result was announced.</span>
            <span>•</span>
            <span>📞 For guidance on alternative admissions and next steps, contact our experts</span>
            <span>•</span>
            <span>🎓 We help you find the best alternative pathways</span>
            <span>•</span>
            <span>📱 Contact: +91 77086 77132 (Enquiry) | +91 93610 27132 (Advisor)</span>
            <span>•</span>
            <span>📧 Email: infonammasewa@gmail.com</span>
            <span>•</span>
          </div>
        </motion.div>
      </div>

      {/* Search and Filter Section */}
      <section className="py-8 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search colleges..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex space-x-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
          
          {/* Statistics */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <h3 className="text-2xl font-bold text-blue-600">
                {getTotalSeats(selectedCategory).toLocaleString()}
              </h3>
              <p className="text-gray-600">Total MBBS Seats</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <h3 className="text-2xl font-bold text-green-600">
                {colleges[selectedCategory as keyof typeof colleges]?.length || 0}
              </h3>
              <p className="text-gray-600">Total Colleges</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <h3 className="text-2xl font-bold text-purple-600">
                {getFeeRange(selectedCategory)}
              </h3>
              <p className="text-gray-600">Fee Range (Annual)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-12 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">
              MBBS Admission Requirements 2025
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Award className="h-8 w-8 text-blue-600" />,
                  title: "NEET Qualification",
                  description: "Must clear NEET with qualifying percentile based on category"
                },
                {
                  icon: <Users className="h-8 w-8 text-green-600" />,
                  title: "Age Criteria",
                  description: "Minimum 17 years and maximum 25 years (30 for reserved categories)"
                },
                {
                  icon: <DollarSign className="h-8 w-8 text-purple-600" />,
                  title: "Documents Required",
                  description: "10th, 12th marksheets, NEET scorecard, ID proof, category certificate"
                }
              ].map((req, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-lg shadow-lg text-center"
                >
                  <div className="flex justify-center mb-4">{req.icon}</div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">{req.title}</h3>
                  <p className="text-gray-600">{req.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Colleges Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-gray-900">
              {selectedCategory} Medical Colleges ({colleges[selectedCategory as keyof typeof colleges]?.length || 0})
            </h2>
            <p className="text-lg text-gray-600">
              {selectedCategory === 'Government' && 'Government medical colleges with subsidized fees and high NEET cutoffs'}
              {selectedCategory === 'Deemed University' && 'Deemed universities with autonomous status and premium medical education'}
              {selectedCategory === 'Self Financing Colleges' && 'Self financing medical colleges including minority institutions'}
            </p>
          </motion.div>

          {filteredColleges.length > 0 && (
            <div className="rounded-lg shadow-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-blue-600 text-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold">S.No.</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">College Name</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Location</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Annual Fees</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Established</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Seats</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">NEET Cutoff</th>
                    <th className="px-6 py-3 text-left text-sm font-semibold">Management</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredColleges.map((college, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}
                    >
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {college.name === "Thanjavur Medical College" ? (
                          <a
                            href="https://www.edufever.com/wp-content/uploads/2021/12/Thanjavur-Medical-College-.webp"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-blue-700 underline hover:text-blue-900"
                          >
                            {college.name}
                          </a>
                        ) : (
                          <div className="font-medium text-gray-900">{college.name}</div>
                        )}
                        {college.minority && (
                          <div className="text-xs text-blue-600 mt-1">{college.minority}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {college.location}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-green-600">
                        {college.fees}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {college.established}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {college.seats}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                          {college.cutoff.replace('NEET: ', '')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          selectedCategory === 'Government' 
                            ? 'bg-green-100 text-green-800'
                            : selectedCategory === 'Deemed University'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {selectedCategory === 'Government' ? 'Govt.' : 
                           selectedCategory === 'Deemed University' ? 'Deemed' : 'Self Financing'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </section>

      {/* Summary Statistics */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-gray-900">
              Tamil Nadu MBBS Statistics 2025
            </h2>
            <p className="text-lg text-gray-600">
              Complete overview of medical education in Tamil Nadu
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg text-center"
            >
              <h3 className="text-4xl font-bold text-blue-600 mb-2">
                {colleges.Government.length}
              </h3>
              <p className="text-gray-600 text-lg mb-4">Government Colleges</p>
              <p className="text-3xl font-semibold text-blue-500">
                {getTotalSeats('Government').toLocaleString()}
              </p>
              <p className="text-gray-500">Total Seats</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg text-center"
            >
              <h3 className="text-4xl font-bold text-green-600 mb-2">
                {colleges['Deemed University'].length}
              </h3>
              <p className="text-gray-600 text-lg mb-4">Deemed Universities</p>
              <p className="text-3xl font-semibold text-green-500">
                {getTotalSeats('Deemed University').toLocaleString()}
              </p>
              <p className="text-gray-500">Total Seats</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg text-center"
            >
              <h3 className="text-4xl font-bold text-purple-600 mb-2">
                {colleges['Self Financing Colleges'].length}
              </h3>
              <p className="text-gray-600 text-lg mb-4">Self Financing Colleges</p>
              <p className="text-3xl font-semibold text-purple-500">
                {getTotalSeats('Self Financing Colleges').toLocaleString()}
              </p>
              <p className="text-gray-500">Total Seats</p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-xl text-center"
          >
            <h3 className="text-5xl font-bold mb-2">74</h3>
            <p className="text-xl mb-2">Total MBBS Colleges</p>
            <p className="text-3xl font-semibold">
              {(getTotalSeats('Government') + getTotalSeats('Deemed University') + getTotalSeats('Self Financing Colleges')).toLocaleString()}
            </p>
            <p className="text-lg opacity-90">Total MBBS Seats in Tamil Nadu</p>
          </motion.div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4 text-gray-900">
              MBBS Admission Process 2025
            </h2>
            <p className="text-lg text-gray-600">
              Step-by-step guide to secure your MBBS admission in Tamil Nadu
            </p>
          </motion.div>

          <div className="space-y-8">
            {[
              { step: 1, title: "NEET Examination", description: "Clear NEET with qualifying marks based on category" },
              { step: 2, title: "Tamil Nadu Counseling", description: "Register for TN state counseling process" },
              { step: 3, title: "Choice Filling", description: "Fill your college and course preferences online" },
              { step: 4, title: "Seat Allotment", description: "Get seat based on NEET rank and preferences" },
              { step: 5, title: "Document Verification", description: "Complete document verification at allotted college" },
              { step: 6, title: "Fee Payment & Admission", description: "Pay fees and confirm admission to secure seat" }
            ].map((process, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-4"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                  {process.step}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{process.title}</h3>
                  <p className="text-gray-600">{process.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MBBSPage;
